import React, {useEffect, useState} from 'react';
import {Alert, Box, Typography} from '@mui/material';
import {
    formatAmount,
    getQE13VO,
    getQI13VO,
    SampleSelectionVariant,
} from '@/services/auditService/materialityService';

export type SamplePlanningBannerProps = {
    open: boolean;
    variant: SampleSelectionVariant;
    companyId: number;
    annualId: number;
    /** Filled rows in the current drawer (transaction or cut-off). */
    currentCount: number;
    /** Label for currentCount, e.g. "Cut-off" or "Transaction". */
    currentLabel: string;
    /** Optional: filled rows in the sibling test drawer for combined context. */
    siblingCount?: number;
    siblingLabel?: string;
    /** QI1.3 monetary population FS code (e.g. PL-TURN-RI). */
    populationCode?: string;
};

const SamplePlanningBanner: React.FC<SamplePlanningBannerProps> = ({
    open,
    variant,
    companyId,
    annualId,
    currentCount,
    currentLabel,
    siblingCount,
    siblingLabel,
    populationCode,
}) => {
    const [suggested, setSuggested] = useState<number | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!open || !companyId || !annualId) return;
        let cancelled = false;
        (async () => {
            setLoading(true);
            setError(null);
            try {
                const data = variant === 'QE1.3'
                    ? await getQE13VO(companyId, annualId)
                    : await getQI13VO(companyId, annualId, populationCode);
                if (!cancelled) {
                    setSuggested(Number(data.actualSampleSize ?? data.sampleSize ?? 0));
                }
            } catch (e: any) {
                if (!cancelled) {
                    setError(e?.message || `Failed to load ${variant}`);
                    setSuggested(null);
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [open, variant, companyId, annualId, populationCode]);

    if (!open) return null;

    const combinedCount = currentCount + (siblingCount ?? 0);
    const hasSibling = siblingCount != null && siblingLabel;

    return (
        <Box sx={{mb: 2}}>
            {error && (
                <Alert severity="warning" sx={{mb: 1}}>
                    {error}
                </Alert>
            )}
            {!error && (
                <Alert severity="info" variant="outlined">
                    <Typography variant="body2" fontWeight={600} gutterBottom>
                        {variant} sample planning (reference)
                    </Typography>
                    <Typography variant="body2">
                        Suggested total sample size:{' '}
                        {loading ? '…' : formatAmount(suggested)}
                    </Typography>
                    <Typography variant="body2">
                        {currentLabel} items entered: {currentCount}
                        {hasSibling && (
                            <> · {siblingLabel} items entered: {siblingCount}</>
                        )}
                        {!loading && suggested != null && suggested > 0 && (
                            <> · Combined: {combinedCount} / {formatAmount(suggested)}</>
                        )}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{mt: 0.5}}>
                        The APM figure is a suggestion only. Allocate samples across transaction and cut-off testing
                        based on professional judgement.
                    </Typography>
                </Alert>
            )}
        </Box>
    );
};

export default SamplePlanningBanner;
